/** Shared shape of the Matrix workspace views: the workspace loads once, every view reads. */

import type {
  CodedMatrix,
  CodedMatrixCell,
  CodedMatrixColumn,
  CodedMatrixRow,
  SessionResult,
  StudyResponse,
} from "./api";
import type { MatrixHypothesisGroup } from "./codedMatrix";

export const MATRIX_VIEWS = [
  { key: "matrix", label: "Matrix" },
  { key: "evidence", label: "Evidence" },
  { key: "responses", label: "Responses" },
] as const;

export type MatrixViewKey = (typeof MATRIX_VIEWS)[number]["key"];

export const DEFAULT_MATRIX_VIEW: MatrixViewKey = "matrix";

export function isMatrixViewKey(value: string | null | undefined): value is MatrixViewKey {
  return MATRIX_VIEWS.some((view) => view.key === value);
}

/** Everything a view needs; the workspace owns loading, the segment filter, and interview numbers. */
export interface MatrixViewContext {
  study: StudyResponse;
  matrix: CodedMatrix;
  /** Qualified sessions in display order, already narrowed by the workspace segment filter. */
  sessions: SessionResult[];
  /** Every qualified session regardless of the segment filter (for bases and cross-tabs). */
  allSessions: SessionResult[];
  interviewNumbers: ReadonlyMap<string, number>;
  rowBySessionId: ReadonlyMap<string, CodedMatrixRow>;
  /** Codebook-ordered hypothesis → question → column groups. */
  groups: MatrixHypothesisGroup[];
  /** Human label for a scope segment id; falls back to the id itself. */
  segmentLabel: (segmentId: string) => string;
  /** Open the shared cell drawer (codes, summary, evidence deep links) for one cell. */
  openCell: (selection: MatrixSelection) => void;
}

export interface MatrixSelection {
  row: CodedMatrixRow;
  column: CodedMatrixColumn;
  cell: CodedMatrixCell | undefined;
  interviewNumber: number | undefined;
}
